import React, { FunctionComponent, PropsWithChildren } from 'react'
import { StyleSheet, useWindowDimensions } from 'react-native'

import { Cocoon, Room } from 'src/state/models/space'
import { fonts } from 'src/theme'

import { buildComponent } from 'src/components/factory'
import { NapCocoon } from 'src/resources/icons/nap-cocoon'

import * as UI from 'native-base'
import * as Icons from 'src/components/icons'
import * as Semantics from 'src/components/semantics'

export const LocationPicker = buildComponent<{
  Icon: FunctionComponent<UI.IBoxProps & { version?: number }>
  left?: boolean
  right?: boolean
  options: (Room | Cocoon)[]
  value: (Room | Cocoon) | null
  onSelect: (selected: (Room | Cocoon) | null) => void
}>()
  .withLocale('screens.connected.book.components.location-picker')
  .withLifecycle(({ props }) => {
    const { width } = useWindowDimensions()
    const selectedOption =
      props.value ?? (props.options.length === 1 ? props.options[0] : null)

    // native-base 3.4.28 wraps the content of a menu item in a padded HStack
    // (its `_stack` prop, missing from the types): without that padding, and
    // with the row taking the whole width, the separator spans the menu again.
    const menuItemProps = {
      p: 0,
      _stack: { px: 0 },
    } as Partial<UI.IMenuItemProps>
    const rowProps = {
      flex: 1,
      alignItems: 'center',
      space: 'xl',
      borderBottomColor: '#0000000E',
      borderBottomWidth: StyleSheet.hairlineWidth,
      py: 3,
      px: 2,
    } as const
    const iconSize = props.Icon === NapCocoon ? 32 : 24

    return {
      width,
      menuItemProps,
      rowProps,
      iconSize,
      selectedOption,
    }
  })
  .withRender(({ props, locale, lifecycle }) => (
    <UI.Menu
      placement={props.left ? 'bottom left' : 'bottom right'}
      width={[lifecycle.width - 16, 550]}
      mx={2}
      mt={2}
      p={0}
      _presenceTransition={{
        animate: {
          transition: { duration: 0, delay: 0, useNativeDriver: true },
        },
      }}
      trigger={(triggerProps) => (
        <Semantics.Button
          {...triggerProps}
          flex={1}
          borderTopLeftRadius={[props.left ? 16 : 0, props.left ? 16 : 0]}
          borderTopRightRadius={[props.left ? 16 : 0, props.right ? 16 : 0]}
          borderBottomLeftRadius={[props.right ? 16 : 0, props.left ? 16 : 0]}
          borderBottomRightRadius={[props.right ? 16 : 0, props.right ? 16 : 0]}
          borderLeftWidth={[0, props.left ? 0 : StyleSheet.hairlineWidth]}
          borderLeftColor="#EEEEEE"
          shadowProps={{
            borderLeftColor: '#CCCCCC',
            borderLeftWidth: props.left ? 0 : StyleSheet.hairlineWidth,
          }}
          marginY={0}
          opaque
          colorScheme="white"
          _stack={{
            flex: 1,
            justifyContent: 'space-between',
          }}
          paddingRight={4}
          rightIcon={
            <>
              <Icons.FontAwesomeIcon
                size="xs"
                icon={Icons.Solid.faChevronDown}
              />
            </>
          }
        >
          <UI.Row alignItems="center" space="xl">
            <props.Icon
              width={24}
              height={24}
              version={(lifecycle.selectedOption as Cocoon)?.version}
            />
            <UI.Text fontSize="xs" numberOfLines={1}>
              {lifecycle.selectedOption && (
                <>
                  {props.Icon === NapCocoon
                    ? (lifecycle.selectedOption as Cocoon)?.version === 4
                      ? ''
                      : locale('cocoon') + ' '
                    : locale('room') + ' '}
                  <UI.Text fontSize="xs" style={fonts.bold} numberOfLines={1}>
                    <Semantics.Localized>
                      {lifecycle.selectedOption.localizedName}
                    </Semantics.Localized>
                  </UI.Text>
                </>
              )}

              {!lifecycle.selectedOption && props.children}
            </UI.Text>
          </UI.Row>
        </Semantics.Button>
      )}
    >
      {props.options.length > 1 && (
        <UI.Menu.Item
          {...lifecycle.menuItemProps}
          onPress={() => props.onSelect(null)}
        >
          <UI.Row {...lifecycle.rowProps}>
            <UI.Box mx={2}>
              <props.Icon
                width={lifecycle.iconSize}
                height={lifecycle.iconSize}
              />
            </UI.Box>
            <UI.Column flexShrink={1}>
              <UI.Text fontSize="xs">{props.children}</UI.Text>
              {props.Icon === NapCocoon && (
                <UI.Text fontSize="2xs" opacity={0.5} numberOfLines={2}>
                  {locale('defaultCocoon')}
                </UI.Text>
              )}
            </UI.Column>
          </UI.Row>
        </UI.Menu.Item>
      )}
      {props.options.map((option) => (
        <UI.Menu.Item
          key={option._id}
          {...lifecycle.menuItemProps}
          onPress={() => props.onSelect(option)}
        >
          <UI.Row {...lifecycle.rowProps}>
            <UI.Box mx={2}>
              <props.Icon
                width={lifecycle.iconSize}
                height={lifecycle.iconSize}
                version={(option as Cocoon).version}
              />
            </UI.Box>
            <UI.Column flexShrink={1}>
              <UI.Text fontSize="xs">
                {props.Icon === NapCocoon
                  ? (option as Cocoon).version === 4
                    ? ''
                    : locale('cocoon') + ' '
                  : locale('room') + ' '}
                <UI.Text fontSize="xs" style={fonts.bold}>
                  <Semantics.Localized>
                    {option.localizedName}
                  </Semantics.Localized>
                </UI.Text>
              </UI.Text>
              <UI.Text fontSize="2xs" opacity={0.5} numberOfLines={2}>
                <Semantics.Localized>
                  {option.localizedDescription}
                </Semantics.Localized>
              </UI.Text>
            </UI.Column>
          </UI.Row>
        </UI.Menu.Item>
      ))}
    </UI.Menu>
  )) as <Value extends Room | Cocoon>(
  props: PropsWithChildren<{
    Icon: FunctionComponent<UI.IBoxProps & { version?: number }>
    left?: boolean
    right?: boolean
    options: Value[]
    value: Value | null
    onSelect: (selected: Value | null) => void
  }>,
) => React.ReactElement
